'use strict';

Object.defineProperty(exports, '__esModule', { value: true });

var React = require('react');
require('../utilities/color.js');
require('date-fns');
var delay = require('../utilities/delay.js');
var fetch = require('../utilities/fetch.js');
require('../utilities/locale.js');

/**
 * A React hook to fetch JSON data
 * @example const deleteExample = useFetch<MyInterface>({ method: 'DELETE', url: `/api/example/${key}` });
 * @example const getExample = useFetch<MyInterface>({ url: `/api/example` });
 * @example const postExample = useFetch<MyInterface>({ method: 'POST', url: `/api/example` });
 * @example const putExample = useFetch<MyInterface>({ method: 'PUT', url: `/api/example/${key}` });
 * @param  {UseFetchOptions} options UseFetchOptions
 * @returns {FetchState<T>} FetchState<T>
 */ const useFetch = (options)=>{
    const { method ='GET' , fetchOnMount =method === 'GET' ? true : false , loadingDelay =0 , url , ...otherOptions } = options;
    const abortControllerRef = React.useRef(null);
    const [fetchState, setFetchState] = React.useState({
        error: undefined,
        fetch: undefined,
        loading: fetchOnMount,
        response: undefined
    });
    const fetchData = async (fetchDataOptions)=>{
        abortControllerRef.current = new AbortController();
        try {
            setFetchState((prevState)=>({
                    ...prevState,
                    error: undefined,
                    loading: true
                }));
            const response = await fetch.fetchJson({
                ...otherOptions,
                method,
                signal: abortControllerRef.current.signal,
                url,
                ...fetchDataOptions
            });
            if (abortControllerRef.current && !abortControllerRef.current.signal.aborted) {
                if (loadingDelay) {
                    setFetchState((prevState)=>({
                            ...prevState,
                            error: undefined,
                            response
                        }));
                    await delay.delay(loadingDelay);
                    setFetchState((prevState)=>({
                            ...prevState,
                            loading: false
                        }));
                } else {
                    setFetchState((prevState)=>({
                            ...prevState,
                            error: undefined,
                            loading: false,
                            response
                        }));
                }
            }
        } catch (error_) {
            const error = error_;
            /**
       * React 18 Strict Mode double loads useEffect, which means the cleanup function below
       * gets called and aborts the first request. This will cause downstream apps
       * to show a flash of error in development mode in between the first fetch and second fetch.
       * This code circumvents this flash of showing an error.
       *
       * This code will have no impact in a production environment and is simply meant to
       * reduce confusion while working locally.
       *
       * https://reactjs.org/docs/strict-mode.html#ensuring-reusable-state
       */ const isDev = process.env.NODE_ENV === 'development';
            const strictModeError = isDev && error?.message === 'The user aborted a request.';
            if (abortControllerRef.current && !abortControllerRef.current.signal.aborted && !strictModeError) {
                if (loadingDelay) {
                    setFetchState((prevState)=>({
                            ...prevState,
                            error,
                            response: undefined
                        }));
                    await delay.delay(loadingDelay);
                    setFetchState((prevState)=>({
                            ...prevState,
                            loading: false
                        }));
                } else {
                    setFetchState((prevState)=>({
                            ...prevState,
                            error,
                            loading: false,
                            response: undefined
                        }));
                }
            }
        }
    };
    React.useEffect(()=>{
        if (fetchOnMount && url) {
            fetchData();
        }
        return ()=>{
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, [
        fetchOnMount,
        url
    ]); // eslint-disable-line react-hooks/exhaustive-deps
    return {
        ...fetchState,
        fetch: fetchData
    };
};

exports.useFetch = useFetch;
//# sourceMappingURL=use-fetch.js.map
